import {
  threadRecordQuery,
  ThreadsQueryThread,
} from "@/app/Threads/GraphQL/threadQueries";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";

/** A thread and the slot it occupied in the list it was held from. */
export type HeldThread = {
  thread: ThreadsQueryThread;
  index: number;
  listKey: string;
};

/**
 * Keeps one thread in the list after it stops being returned for it.
 *
 * Only ever the thread being read, and only when something the reader did not
 * aim at the list moved it — reopening a closed thread by replying to it. An
 * explicit close or reopen moves the selection on instead, so nothing is held
 * and the row goes.
 *
 * The hold carries the row it names, captured while it was still listed, so
 * nothing here has to follow the list to find it once it has gone.
 */
export function useHeldThread(
  threads: ThreadsQueryThread[],
  selectedId: string | null,
  held: HeldThread | null,
  listKey: string,
): ThreadsQueryThread[] {
  // A hold belongs to the list it was taken from, and to the thread still being
  // read. Anything else leaves it inert until it is replaced.
  const isHolding =
    held !== null && held.listKey === listKey && held.thread.id === selectedId;

  // Already fetched by the open thread's own header, so this reads its cache.
  const { data } = useGqlQuery(
    threadRecordQuery,
    { id: selectedId ?? "" },
    { enabled: isHolding },
  );

  if (!isHolding || threads.some((thread) => thread.id === selectedId)) {
    return threads;
  }

  // What dropped the thread from the list is a change to the thread itself, so
  // the held copy is from before it — the record query is the only one still
  // following the thread.
  const thread = data?.thread ?? held.thread;
  const listed = [...threads];
  listed.splice(Math.min(held.index, listed.length), 0, thread);

  return listed;
}
